import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { NotificationSettings as NotificationSettingsQuery } from "../../graphql/queries/notificationSettings";
import { RegisterSubscriber } from "../../graphql/mutations/registerSubscriber";
import { NotificationSettings } from "../../codegen/graphql";

export interface NovuSubscriber {
    subscriberId: string;
    applicationId: string;
    hmacHash: string;
}

interface NotificationsContextValue {
    subscriber: NovuSubscriber | null;
    settings: NotificationSettings | null;
    loading: boolean;
    /** True while we're auto-registering the subscriber on first use. */
    initializing: boolean;
    error: Error | null;
    refetchSettings: () => Promise<unknown>;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export const useNotificationsContext = (): NotificationsContextValue => {
    const ctx = useContext(NotificationsContext);
    if (!ctx) {
        throw new Error("useNotificationsContext must be used inside <NotificationsProvider>");
    }
    return ctx;
};

export const useNovuSubscriber = (): NovuSubscriber | null => useNotificationsContext().subscriber;

interface Props {
    children: ReactNode;
}

const NotificationsProvider = ({ children }: Props) => {
    const settingsQuery = useQuery(NotificationSettingsQuery, {
        fetchPolicy: "cache-and-network",
        // Keep partial data when the backend returns "Subscriber not found" so we can
        // detect the unregistered case (data.notificationSettings === null) and react.
        errorPolicy: "all",
    });
    const [registerSubscriber] = useMutation(RegisterSubscriber);
    const registrationAttempted = useRef(false);
    const [initializing, setInitializing] = useState(false);

    const settings = settingsQuery.data?.notificationSettings ?? null;
    const isMissing = !settingsQuery.loading && !settings;

    useEffect(() => {
        if (!isMissing) return;
        if (registrationAttempted.current) return;
        // Latch the attempt before the network call so we never fire the mutation more
        // than once per session — even on registration failure. The backend is idempotent
        // for already-registered users, so triggering it on any "settings missing" state
        // is safe; we just don't want to retry blindly on unrelated errors (auth, network).
        // User can reload to retry.
        registrationAttempted.current = true;
        setInitializing(true);
        registerSubscriber()
            .then(() => settingsQuery.refetch())
            .catch(err => {
                console.error("Failed to register notification subscriber", err);
            })
            .finally(() => setInitializing(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMissing]);

    const subscriber: NovuSubscriber | null = settings?.subscriber
        ? {
              subscriberId: settings.subscriber.id,
              applicationId: settings.subscriber.applicationId,
              hmacHash: settings.subscriber.hmacHash,
          }
        : null;

    const value: NotificationsContextValue = {
        subscriber,
        settings: settings as NotificationSettings | null,
        loading: settingsQuery.loading,
        initializing,
        error: (settingsQuery.error as Error | undefined) ?? null,
        refetchSettings: settingsQuery.refetch,
    };

    return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export default NotificationsProvider;
